import { resolveExecutor, getLogger, type GqlExecutor } from '@hopdrive/sdk-core'
import type { GeocodeResult } from '../../types'
import { getDefaultGeoProviders } from '../../init'
import type { GeocodeResult as GeoGeocodeResult } from '../../geo/types/geocoding'
import { findGeocodeCache } from './findGeocodeCache'
import { storeGeocodeCacheAsync } from './storeGeocodeCache'

export interface GeocodeAddressParams {
  address: string
  placeId?: string
  executor?: GqlExecutor
}

/**
 * Geocode an address using Google Geocoding API.
 *
 * When a `placeId` is provided, uses the Places API (New) Place Details
 * endpoint instead of the legacy Geocoding API. This is necessary for
 * browser environments where API keys have HTTP referer restrictions
 * (the legacy Geocoding API rejects such keys).
 *
 * First checks the global geocode cache. If not found, calls Google API
 * and stores the result in cache for future use.
 *
 * @see location-matching-algorithm.md#geocoding-error-handling
 * @see performance-optimizations.md#4-global-geocoding-cache
 */
export async function geocodeAddress(
  address: string,
  executor?: GqlExecutor,
  placeId?: string
): Promise<GeocodeResult> {
  const exec = resolveExecutor(executor)

  // Check global geocode cache first
  const cached = await findGeocodeCache({ address, executor: exec })
  if (cached) {
    getLogger().debug('geocode.cacheHit', { address })
    return cached
  }

  // Cache miss - call Google API via geo providers
  const { geocoding } = getDefaultGeoProviders()
  const apiStart = Date.now()

  let geoResult
  if (placeId && geocoding.fetchPlaceDetails) {
    // Use Places API (New) when placeId is available — works with browser keys
    geoResult = await geocoding.fetchPlaceDetails(placeId)
  } else {
    geoResult = await geocoding.geocode({ address })
  }

  getLogger().debug('geocode.apiCall', { address, placeId, apiMs: Date.now() - apiStart })

  // Map geo module result to local GeocodeResult type
  const result = mapGeoResultToLocal(geoResult)

  // Store in cache asynchronously (fire-and-forget)
  storeGeocodeCacheAsync({
    address,
    geocodeResult: result,
    executor: exec,
  })

  return result
}

/**
 * Map geo module GeocodeResult to local GeocodeResult type.
 * The local type is simpler and used throughout the lanes package.
 */
function mapGeoResultToLocal(geoResult: GeoGeocodeResult): GeocodeResult {
  return {
    streetAddress: geoResult.streetAddress,
    formattedAddress: geoResult.formattedAddress,
    latitude: geoResult.latitude,
    longitude: geoResult.longitude,
    placeId: geoResult.placeId,
    plusCode: geoResult.plusCode,
    components: {
      streetNumber: geoResult.components.streetNumber,
      route: geoResult.components.route,
      city: geoResult.components.city,
      state: geoResult.components.state,
      postalCode: geoResult.components.postalCode,
      country: geoResult.components.country,
    },
  }
}
